import { useEffect, useRef } from 'react';
import { CheckCircle2, LoaderCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { useInquiry } from '../hooks/useInquiry';

export const InquiryForm = ({ onDone, projectContext = '', descriptionLabel = 'Project description' }) => {
  const { busy, error, receipt, submit } = useInquiry();
  const alertRef = useRef(null);
  const receiptRef = useRef(null);
  useEffect(() => { if (error) alertRef.current?.focus(); }, [error]);
  useEffect(() => { if (receipt) receiptRef.current?.focus(); }, [receipt]);

  if (receipt) return (
    <section className="inquiry-confirmation" data-testid="inquiry-confirmation"
      tabIndex={-1} ref={receiptRef} role="status" aria-live="polite">
      <CheckCircle2 size={32} aria-hidden="true" />
      <h2 data-testid="inquiry-confirmation-title">Inquiry received</h2>
      <p data-testid="inquiry-confirmation-message">Thank you. Your inquiry has been saved.</p>
      <p className="receipt-reference" data-testid="inquiry-reference">Reference: {receipt.id}</p>
      <Button type="button" data-testid="inquiry-done" onClick={onDone}>Back to Home</Button>
    </section>
  );

  return (
    <form className="inquiry-form" data-testid="inquiry-form" onSubmit={submit} aria-busy={busy}>
      {projectContext && <><p className="inquiry-context" data-testid="inquiry-context">{projectContext}</p><input type="hidden" name="project_context" value={projectContext} data-testid="inquiry-context-input" /></>}
      <fieldset disabled={busy} className="inquiry-fields">
        <div className="form-field">
          <Label htmlFor="inquiry-name" data-testid="inquiry-name-label">Name <span aria-hidden="true">*</span></Label>
          <Input id="inquiry-name" name="name" data-testid="inquiry-name" autoComplete="name" maxLength={120} required />
        </div>
        <div className="form-field">
          <Label htmlFor="inquiry-email" data-testid="inquiry-email-label">Email</Label>
          <Input id="inquiry-email" name="email" type="email" data-testid="inquiry-email" autoComplete="email" maxLength={254} aria-describedby="contact-requirement" />
        </div>
        <div className="form-field">
          <Label htmlFor="inquiry-phone" data-testid="inquiry-phone-label">Phone</Label>
          <Input id="inquiry-phone" name="phone" type="tel" data-testid="inquiry-phone" autoComplete="tel" maxLength={40} aria-describedby="contact-requirement" />
          <p id="contact-requirement" className="field-note" data-testid="contact-requirement">Email or phone is required.</p>
        </div>
        <div className="form-field">
          <Label htmlFor="inquiry-project" data-testid="inquiry-project-label">{descriptionLabel} <span aria-hidden="true">*</span></Label>
          <Textarea id="inquiry-project" name="project_description" data-testid="inquiry-project" rows={5} maxLength={5000 - (projectContext ? projectContext.length + 2 : 0)} required />
        </div>
      </fieldset>
      {error && <p ref={alertRef} tabIndex={-1} className="form-error" role="alert" data-testid="inquiry-error">{error}</p>}
      <Button type="submit" disabled={busy} className="inquiry-submit" data-testid="inquiry-submit">
        {busy && <LoaderCircle size={16} className="animate-spin" aria-hidden="true" />}
        {busy ? 'Submitting…' : 'Submit Inquiry'}
      </Button>
    </form>
  );
};